import { z } from 'zod';

import { nonNegativeIntSchema, positiveIntSchema } from '../common/primitives';

/**
 * Kapazität und Belegung eines Worker-Nodes.
 *
 * Zwei streng getrennte Begriffe:
 *
 * - `capacity`  = was der Server physisch hat. Wird vom Worker gemeldet.
 * - `allocated` = Summe der zugesicherten Kontingente aller zugewiesenen Sites.
 *                 Wird ausschließlich vom Master gepflegt.
 *
 * Die Differenz ist die für den Scheduler verfügbare Kapazität. Sie muss
 * bereits beim Zuweisen reserviert werden, nicht erst wenn der Container läuft
 * — sonst landen bei zwei gleichzeitigen Bestellungen beide auf demselben Node.
 */

/** Physische Ausstattung eines Nodes, vom Agent beim Enrollment gemeldet. */
export const nodeCapacitySchema = z.object({
  /** Anzahl logischer CPU-Kerne. */
  cpuCores: positiveIntSchema,
  /** Gesamter Arbeitsspeicher in Mebibyte. */
  memoryMb: positiveIntSchema,
  /** Nutzbarer Plattenplatz des Datenverzeichnisses in Gibibyte. */
  diskGb: positiveIntSchema,
  /**
   * Obergrenze gleichzeitig gehosteter Sites.
   * Harte Grenze unabhängig von RAM/Disk — schützt vor Container-Explosion
   * und den daraus folgenden Docker-Daemon-Problemen.
   */
  maxSites: positiveIntSchema,
});

export type NodeCapacity = z.infer<typeof nodeCapacitySchema>;

/** Vom Master gebuchte Reservierungen auf einem Node. */
export const nodeAllocationSchema = z.object({
  /** Summe der zugesicherten CPU-Anteile (1000 = ein voller Kern). */
  cpuMilli: nonNegativeIntSchema,
  /** Summe des zugesicherten Arbeitsspeichers in Mebibyte. */
  memoryMb: nonNegativeIntSchema,
  /** Summe des zugesicherten Plattenplatzes in Gibibyte. */
  diskGb: nonNegativeIntSchema,
  /** Anzahl zugewiesener Sites. */
  siteCount: nonNegativeIntSchema,
});

export type NodeAllocation = z.infer<typeof nodeAllocationSchema>;

/** Ressourcenbedarf, den eine zu platzierende Site anmeldet. */
export const resourceRequestSchema = z.object({
  cpuMilli: positiveIntSchema,
  memoryMb: positiveIntSchema,
  diskGb: positiveIntSchema,
});

export type ResourceRequest = z.infer<typeof resourceRequestSchema>;

/** Neutrale Ausgangsbelegung eines frisch registrierten Nodes. */
export const EMPTY_ALLOCATION: NodeAllocation = {
  cpuMilli: 0,
  memoryMb: 0,
  diskGb: 0,
  siteCount: 0,
};

/**
 * Freie Kapazität = Ausstattung minus Reservierungen.
 *
 * Ergebnisse werden bei 0 abgeschnitten. Eine Überbuchung (etwa nach dem
 * Verkleinern eines Servers) darf nicht als negative Zahl weiterlaufen, sonst
 * kippen die Verhältniswerte im Scheduler-Scoring ins Absurde.
 */
export function calculateFreeCapacity(
  capacity: NodeCapacity,
  allocated: NodeAllocation,
): {
  cpuMilli: number;
  memoryMb: number;
  diskGb: number;
  siteSlots: number;
} {
  return {
    cpuMilli: Math.max(0, capacity.cpuCores * 1000 - allocated.cpuMilli),
    memoryMb: Math.max(0, capacity.memoryMb - allocated.memoryMb),
    diskGb: Math.max(0, capacity.diskGb - allocated.diskGb),
    siteSlots: Math.max(0, capacity.maxSites - allocated.siteCount),
  };
}

/** Prüft, ob eine Anforderung in die freie Kapazität eines Nodes passt. */
export function fitsIntoCapacity(
  capacity: NodeCapacity,
  allocated: NodeAllocation,
  request: ResourceRequest,
): boolean {
  const free = calculateFreeCapacity(capacity, allocated);

  return (
    free.cpuMilli >= request.cpuMilli &&
    free.memoryMb >= request.memoryMb &&
    free.diskGb >= request.diskGb &&
    free.siteSlots >= 1
  );
}

/** Addiert eine Reservierung auf die bestehende Belegung. */
export function addAllocation(allocated: NodeAllocation, request: ResourceRequest): NodeAllocation {
  return {
    cpuMilli: allocated.cpuMilli + request.cpuMilli,
    memoryMb: allocated.memoryMb + request.memoryMb,
    diskGb: allocated.diskGb + request.diskGb,
    siteCount: allocated.siteCount + 1,
  };
}

/**
 * Gibt eine Reservierung wieder frei.
 * Schneidet bei 0 ab, damit ein doppelt ausgeführtes Freigeben (etwa durch
 * einen Job-Retry) die Belegung nicht ins Negative zieht.
 */
export function removeAllocation(
  allocated: NodeAllocation,
  request: ResourceRequest,
): NodeAllocation {
  return {
    cpuMilli: Math.max(0, allocated.cpuMilli - request.cpuMilli),
    memoryMb: Math.max(0, allocated.memoryMb - request.memoryMb),
    diskGb: Math.max(0, allocated.diskGb - request.diskGb),
    siteCount: Math.max(0, allocated.siteCount - 1),
  };
}
